import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Music,
  PartyPopper,
  Sparkles,
  Star,
} from "lucide-react";
import { api, mediaUrl } from "../api/client";
import VenueCard from "../components/VenueCard";

const steps = [
  {
    title: "Fall for a venue",
    copy: "Browse hotels and halls with their own pages, colors and character.",
    icon: Heart,
    tone: "bg-blush text-plum",
  },
  {
    title: "Reserve the night",
    copy: "Book a stay, a wedding package, or a hall that turns into a party.",
    icon: PartyPopper,
    tone: "bg-coral/80 text-ink",
  },
  {
    title: "Celebrate in color",
    copy: "Owners confirm, planners polish, and Aurelia keeps the glow on.",
    icon: Music,
    tone: "bg-lilac/80 text-ink",
  },
];

export default function Home() {
  const [venues, setVenues] = useState([]);

  useEffect(() => {
    api("/api/venues")
      .then(setVenues)
      .catch(() => setVenues([]));
  }, []);

  const featured = venues.filter((v) => v.featured).slice(0, 4);
  const rest = featured.length ? featured : venues.slice(0, 4);
  const heroCovers = rest.map((venue) => venue.coverImage).filter(Boolean);
  const heroLeft =
    mediaUrl(heroCovers[0]) ||
    "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=900&q=80";
  const heroRight =
    mediaUrl(heroCovers[1]) ||
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=80";

  return (
    <div>
      <section className="relative overflow-hidden px-4 pb-20 pt-10 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-blush/70 blur-3xl" />
        <div className="pointer-events-none absolute right-0 top-0 h-80 w-80 rounded-full bg-lilac/50 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-teal/30 blur-3xl" />

        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="chip bg-ink text-gold">Hotels · halls · after dark</p>
            <h1 className="mt-6 font-serif text-5xl leading-[1.05] sm:text-6xl lg:text-7xl">
              Celebrate in rooms
              <span className="italic text-rose"> that remember.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink/70">
              Aurelia is a boutique marketplace for hotel stays and wedding
              halls — each owner has a page of their own, dressed for vows,
              parties, and slow golden mornings.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/venues" className="btn-rose">
                Browse venues <ArrowRight size={16} />
              </Link>
              <Link to="/login" className="btn-gold">
                Owner studio
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-8 text-sm">
              <Stat n={`${venues.length}+`} label="House pages" />
              <Stat n="3 cities" label="and counting" />
              <Stat n="Color-first" label="wedding energy" />
            </div>
          </div>
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <img
                src={heroLeft}
                alt={rest[0]?.name || "Wedding table"}
                className="h-64 w-full rounded-[2rem] object-cover shadow-card sm:h-80"
              />
              <img
                src={heroRight}
                alt={rest[1]?.name || "Hotel lobby"}
                className="mt-10 h-64 w-full rounded-[2rem] object-cover shadow-gold sm:h-80"
              />
            </div>
            <div className="absolute -bottom-6 left-6 right-6 rounded-3xl bg-ink px-5 py-4 text-cream shadow-glow sm:left-10 sm:right-auto">
              <p className="flex items-center gap-2 text-sm text-gold">
                <Sparkles size={16} /> Tonight’s mood
              </p>
              <p className="font-serif text-xl">Champagne, drums, disco pink.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-2">
          <Link
            to="/venues?type=hotel"
            className="overflow-hidden rounded-[2rem] bg-teal p-8 text-ink shadow-card transition hover:-translate-y-1"
          >
            <p className="text-xs uppercase tracking-[0.22em]">Sleep over</p>
            <h2 className="mt-3 font-serif text-4xl">Hotels with a pulse</h2>
            <p className="mt-3 max-w-sm text-sm text-ink/70">
              Ocean villas, townhouse suites, tea-country lofts — stay where the
              party started.
            </p>
          </Link>
          <Link
            to="/venues?type=wedding-hall"
            className="overflow-hidden rounded-[2rem] bg-plum p-8 text-cream shadow-card transition hover:-translate-y-1"
          >
            <p className="text-xs uppercase tracking-[0.22em] text-gold">Say yes</p>
            <h2 className="mt-3 font-serif text-4xl">Wedding halls</h2>
            <p className="mt-3 max-w-sm text-sm text-cream/75">
              Ballrooms, lagoon aisles and glass halls that become nightclubs
              after cake.
            </p>
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-rose">Featured houses</p>
            <h2 className="font-serif text-4xl">Every owner, a world</h2>
          </div>
          <Link to="/venues" className="btn-ghost">
            See all
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
          {rest.map((venue) => (
            <VenueCard key={venue.id} venue={venue} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <h2 className="mb-8 font-serif text-4xl">How Aurelia glows</h2>
        <div className="grid gap-5 md:grid-cols-3">
          {steps.map((step) => (
            <div key={step.title} className="card p-6">
              <span className={`grid h-12 w-12 place-items-center rounded-2xl ${step.tone}`}>
                <step.icon size={20} />
              </span>
              <h3 className="mt-4 font-serif text-2xl">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink/65">{step.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2.2rem] bg-ink px-6 py-12 text-cream sm:px-12">
          <div className="grid items-center gap-8 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <p className="flex items-center gap-2 text-gold">
                <Star size={16} /> Loved by celebrants
              </p>
              <blockquote className="mt-4 font-serif text-3xl leading-snug sm:text-4xl">
                “It felt like the hall had been waiting for us — gold light,
                drums, then a dance floor that would not quit.”
              </blockquote>
              <p className="mt-4 text-sm text-cream/60">Priya & Arjun · The Orchid Colombo</p>
            </div>
            <img
              src="https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=1000&q=80"
              alt="Party lights"
              className="h-56 w-full rounded-[1.8rem] object-cover sm:h-72"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <p className="font-serif text-2xl">{n}</p>
      <p className="text-ink/50">{label}</p>
    </div>
  );
}
